'use client';

import InfiniteScroll from 'react-infinite-scroll-component';
import useSWRInfinite from 'swr/infinite';

import { LoadingStar } from '~/components/common/loading-star';
import { SearchBlogPostsResponse, SearchBlogsQueryVariables } from '~/server/types';

import { listBlogPostsAction } from './actions';
import { BlogPostTeaser, BlogPostTeaserSkeleton } from './blog-post-teaser';

interface InfiniteBlogPostsProps {
  initialData: SearchBlogPostsResponse;
  queryVariables: SearchBlogsQueryVariables;
  isFallback?: never;
}

interface FallbackProps {
  isFallback: true;
}

export function InfiniteBlogPostsFallback() {
  return (
    <section className='container xl:max-w-7xl'>
      <h1 className='sr-only'>Recent Posts</h1>
      <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8'>
        {[...Array(3)].map((_, i) => (
          <BlogPostTeaserSkeleton key={i} />
        ))}
      </div>
    </section>
  );
}

function InfiniteBlogPostsContent({ initialData, queryVariables }: InfiniteBlogPostsProps) {
  const { data, size, setSize, isLoading } = useSWRInfinite(
    (_, previousPageData) => {
      return [`Posts`, previousPageData?.nextCursor || 0, queryVariables];
    },
    async ([, cursor]) => {
      const { data, error } = await listBlogPostsAction({
        ...queryVariables,
        cursor,
      });

      if (error) {
        throw new Error(error.toString());
      }

      return data;
    },
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      initialSize: 1,
      fallbackData: [initialData],
    },
  );

  const blogPosts = data?.flatMap((p) => p?.posts || []) || [];

  const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const atEnd = data?.[data.length - 1]?.nextCursor === null;

  return (
    <section className='container xl:max-w-7xl'>
      <h2 className='sr-only'>Recent Posts</h2>
      <InfiniteScroll
        className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8'
        dataLength={blogPosts?.length || 0}
        hasMore={!atEnd}
        loader={
          <div className='col-span-full mt-8 text-center'>
            <LoadingStar />
          </div>
        }
        next={() => !isLoadingMore && setSize(size + 1)}
      >
        {blogPosts.map((blogPost) => (
          <BlogPostTeaser key={blogPost.sys.id} post={blogPost} hideCategory={!!queryVariables.category} />
        ))}
      </InfiniteScroll>
    </section>
  );
}

export function InfiniteBlogPosts(props: InfiniteBlogPostsProps | FallbackProps) {
  return props.isFallback ? <InfiniteBlogPostsFallback /> : <InfiniteBlogPostsContent {...props} />;
}
